<template>
  <div class="tab">
    <div class="datos">
      <div class="dato">
        <span class="lbl">Área responsable</span>
        <PortalBadge :text="plan.area" :color="colorArea(plan.area)" />
      </div>
      <div class="dato">
        <span class="lbl">Responsable</span>
        <span class="val" :class="{ pendiente: !plan.responsable }">
          {{ plan.responsable || "Sin designar" }}
        </span>
      </div>
      <div class="dato">
        <span class="lbl">Gerente</span>
        <span class="val">{{ plan.gerente || "—" }}</span>
      </div>
      <div class="dato">
        <span class="lbl">Prioridad</span>
        <span class="val">{{ plan.prioridad || "—" }}</span>
      </div>
      <div class="dato">
        <span class="lbl">Fecha compromiso</span>
        <span class="val">{{ fmtFecha(plan.fechaCompromiso) }}</span>
      </div>
      <!-- RN-05: la original solo aparece si hubo un cambio aprobado. Mostrarla
           siempre sería ruido; ocultarla cuando cambió sería perder la huella. -->
      <div v-if="fechaMovida" class="dato">
        <span class="lbl">Fecha original</span>
        <span class="val tachada">{{ fmtFecha(plan.fechaCompromisoOriginal) }}</span>
      </div>
      <div class="dato">
        <span class="lbl">Asignado</span>
        <span class="val">{{ fmtFecha(plan.fechaAsignacion) }}</span>
      </div>
      <div class="dato">
        <span class="lbl">Aceptado</span>
        <span class="val">{{ fmtFecha(plan.fechaAceptacion) }}</span>
      </div>
    </div>

    <div v-if="plan.descripcion" class="bloque">
      <div class="bloque-titulo">Descripción</div>
      <p class="texto">{{ plan.descripcion }}</p>
    </div>

    <div v-if="plan.motivoRetraso" class="bloque alerta">
      <div class="bloque-titulo">Último rechazo / motivo de retraso</div>
      <p class="texto">{{ plan.motivoRetraso }}</p>
    </div>

    <!-- Análisis de causa raíz. Registrarla es lo que mueve el plan de
         "aceptado" a "en análisis" (lo hace el backend, no esta pantalla). -->
    <div class="bloque">
      <div class="bloque-titulo">Análisis de causa raíz</div>

      <template v-if="editable">
        <label class="field">
          <span class="field-label">Causa raíz</span>
          <textarea
            v-model="f.causaRaiz"
            rows="3"
            class="inp"
            placeholder="Por qué ocurrió, no qué ocurrió"
          ></textarea>
          <span v-if="errores.causaRaiz" class="field-error">{{ errores.causaRaiz }}</span>
        </label>
        <div class="grid-2">
          <label class="field">
            <span class="field-label">Acción de contención</span>
            <textarea
              v-model="f.accionContencion"
              rows="2"
              class="inp"
              placeholder="Qué se hizo de inmediato (opcional)"
            ></textarea>
          </label>
          <label class="field">
            <span class="field-label">Solución definitiva</span>
            <textarea
              v-model="f.solucionDefinitiva"
              rows="2"
              class="inp"
              placeholder="Qué evita que vuelva a pasar (opcional)"
            ></textarea>
          </label>
        </div>
        <button
          class="btn"
          :disabled="guardando"
          :style="{ background: guardando ? '#cbd5e1' : '#003ca5' }"
          @click="guardar"
        >
          {{ guardando ? "Guardando…" : "Guardar análisis" }}
        </button>
      </template>

      <template v-else>
        <div class="sub">
          <span class="sub-lbl">Causa raíz</span>
          <p class="texto">{{ plan.causaRaiz || "Sin registrar." }}</p>
        </div>
        <div v-if="plan.accionContencion" class="sub">
          <span class="sub-lbl">Acción de contención</span>
          <p class="texto">{{ plan.accionContencion }}</p>
        </div>
        <div v-if="plan.solucionDefinitiva" class="sub">
          <span class="sub-lbl">Solución definitiva</span>
          <p class="texto">{{ plan.solucionDefinitiva }}</p>
        </div>
      </template>
    </div>

    <div v-if="plan.resultadoEsperado || plan.criteriosAceptacion" class="bloque">
      <div class="bloque-titulo">Compromiso</div>
      <div v-if="plan.resultadoEsperado" class="sub">
        <span class="sub-lbl">Resultado esperado</span>
        <p class="texto">{{ plan.resultadoEsperado }}</p>
      </div>
      <div v-if="plan.criteriosAceptacion" class="sub">
        <span class="sub-lbl">Criterios de aceptación</span>
        <p class="texto">{{ plan.criteriosAceptacion }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import PortalBadge from "../PortalBadge.vue";
import { colorArea } from "../portal.helpers";
import { fmtFecha } from "../cx.rules";
import { validaCausaRaiz } from "../cx.validators";

export default {
  name: "TabResumen",
  components: { PortalBadge },
  props: {
    plan: { type: Object, required: true },
    editable: { type: Boolean, default: false },
    guardando: { type: Boolean, default: false },
  },
  emits: ["guardar-causa"],
  data() {
    return {
      errores: {},
      f: {
        causaRaiz: this.plan.causaRaiz || "",
        accionContencion: this.plan.accionContencion || "",
        solucionDefinitiva: this.plan.solucionDefinitiva || "",
      },
    };
  },
  computed: {
    fechaMovida() {
      return (
        this.plan.fechaCompromisoOriginal &&
        this.plan.fechaCompromisoOriginal !== this.plan.fechaCompromiso
      );
    },
  },
  methods: {
    colorArea,
    fmtFecha,
    guardar() {
      if (this.guardando) return;
      const r = validaCausaRaiz(this.f);
      this.errores = r.errores;
      if (!r.ok) return;
      this.$emit("guardar-causa", { ...this.f });
    },
  },
};
</script>

<style scoped>
.tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.datos {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  background: var(--pt-elev);
  border-radius: 12px;
  padding: 14px;
}
.dato {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.lbl {
  font-size: 11px;
  font-weight: 600;
  color: var(--pt-muted);
  text-transform: uppercase;
}
.val {
  font-size: 14px;
  color: var(--pt-text);
}
.tachada {
  text-decoration: line-through;
  color: var(--pt-muted);
}
.bloque {
  border: 1px solid var(--pt-border);
  border-radius: 12px;
  padding: 14px;
}
.alerta {
  border-color: #fcd34d;
}
.bloque-titulo {
  font-size: 12px;
  font-weight: 700;
  color: var(--pt-dim);
  text-transform: uppercase;
  margin-bottom: 10px;
}
.sub {
  margin-bottom: 10px;
}
.sub-lbl {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: var(--pt-muted);
  text-transform: uppercase;
  margin-bottom: 2px;
}
.texto {
  margin: 0;
  font-size: 14px;
  color: var(--pt-dim);
  white-space: pre-wrap;
}
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.field {
  display: block;
  margin-bottom: 10px;
}
.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--pt-dim);
  margin-bottom: 4px;
}
.inp {
  width: 100%;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  border: 1px solid var(--pt-border);
  outline: none;
  background: var(--pt-card);
  color: var(--pt-text);
  box-sizing: border-box;
  font-family: inherit;
  resize: none;
}
.inp:focus {
  border-color: #003ca5;
}
.field-error {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #b91c1c;
}
.btn {
  padding: 9px 18px;
  border: 0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  font-family: inherit;
}
.btn:disabled {
  cursor: not-allowed;
}
.pendiente {
  color: var(--pt-muted);
  font-style: italic;
}
</style>
